import { useEffect, useRef, useState } from "react";
import { useBrowserDismiss } from './ui/browser-navigation';
import { RESOURCE_GROUPS, compactWheelTagName, wheelTagNames } from "@minglun/domain";
import type { Character, ResourceGroup, SolverResult, SsrChoiceSuggestion, UserPlan, Wheel, Workbench } from "@minglun/domain";
import type { SolverProgressV2 } from "@minglun/protocol";

export type Page =
  | "personal"
  | "study"
  | "objectives"
  | "base"
  | "resources"
  | "solver"
  | "results"
  | "characters"
  | "wheels"
  | "global-settings"
  | "logs";

export const studyEditorPages: { id: Page; label: string }[] = [
  { id: "objectives", label: "目标" },
  { id: "base", label: "参考" },
  { id: "resources", label: "输入" },
  { id: "solver", label: "计算" },
];

export const userPages: { id: Page; label: string }[] = [
  { id: "study", label: "方案" },
  ...studyEditorPages,
  { id: "results", label: "结果" },
];

export const isStudyPage = (page: Page): boolean =>
  page === "study" || studyEditorPages.some((item) => item.id === page);

export const developerPages: { id: Page; label: string }[] = [
  { id: "characters", label: "命轮" },
  { id: "wheels", label: "关系命轮" },
  { id: "global-settings", label: "设置" },
  { id: "logs", label: "运行日志" },
];

export interface AppSnapshot {
  workbench: Workbench;
  userPlan: UserPlan;
}

export interface ConfirmRequest {
  title: string;
  message: string;
  confirmLabel?: string;
  onConfirm(): void;
}

export interface MigrationRequest {
  kind: "方案" | "结果";
  count: number;
  warnings: string[];
  defaultsAdded: number;
  remapped: number;
  onConfirm(): void;
}

export interface WheelBasics {
  name: string;
  wheelCategoryId: string;
  maxStar: number;
}

export interface WheelBasicsDialogState {
  mode: "create" | "duplicate" | "edit";
  initial: WheelBasics;
}

export type ResizeDirection = "n" | "ne" | "e" | "se" | "s" | "sw" | "w" | "nw";

export const clone = <T,>(value: T): T => structuredClone(value);

export const formatNumber = (value: number, digits = 2): string =>
  new Intl.NumberFormat("zh-CN", { maximumFractionDigits: digits }).format(
    value,
  );

export const formatCompactNumber = (value: number): string =>
  new Intl.NumberFormat("zh-CN", {
    notation: Math.abs(value) >= 10_000 ? "compact" : "standard",
    maximumFractionDigits: Math.abs(value) >= 10_000 ? 2 : 0,
  }).format(value);

export const formatDate = (value: string | undefined): string => {
  if (!value) return "授权有效";
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? `有效期至 ${new Intl.DateTimeFormat("zh-CN", { year: "numeric", month: "2-digit", day: "2-digit" }).format(date)}`
    : "授权有效";
};

export const formatElapsedSeconds = (milliseconds: number): string => {
  const seconds = Math.max(0, Math.floor(milliseconds / 1000));
  return `${seconds} 秒`;
};

export function normalizeSsrChoiceSuggestions(
  result: SolverResult,
  workbench: Workbench,
  requestedTotal: number,
): SsrChoiceSuggestion[] {
  const total = Math.max(0, Math.floor(requestedTotal));
  if (!total) return [];
  const candidates = workbench.characters
    .filter((character) => character.isEnabled && character.rarity === "SSR")
    .sort((left, right) => left.order - right.order || left.id.localeCompare(right.id));
  if (!candidates.length) return [];
  const candidateIds = new Set(candidates.map((character) => character.id));
  const allocation = new Map<string, number>();
  let remaining = total;
  for (const item of result.ssrChoiceSuggestions ?? []) {
    if (!candidateIds.has(item.characterId) || remaining <= 0) continue;
    const count = Math.min(remaining, Math.max(0, Math.floor(item.count)));
    if (!count) continue;
    allocation.set(item.characterId, (allocation.get(item.characterId) ?? 0) + count);
    remaining -= count;
  }
  return candidates.map((character) => ({ characterId: character.id, count: allocation.get(character.id) ?? 0 })).filter((item) => item.count > 0);
}

export function useSolverElapsed(progress: SolverProgressV2 | undefined): number | undefined {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (progress?.startedAt === undefined) return undefined;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [progress?.startedAt]);
  if (progress?.startedAt === undefined) return undefined;
  return Math.max(0, now - progress.startedAt);
}

export const newId = (prefix: string): string =>
  `${prefix}.${crypto.randomUUID().slice(0, 8)}`;

export const categories = (
  workbench: Workbench,
): Map<string, Workbench["wheelCategories"][number]> =>
  new Map(workbench.wheelCategories.map((item) => [item.id, item]));

export const membersByCharacterOrder = (
  wheel: Wheel,
  characters: Character[],
): Wheel["members"] => {
  const orderById = new Map(
    characters.map((character) => [character.id, character.order]),
  );
  return [...wheel.members].sort(
    (left, right) =>
      (orderById.get(left.characterId) ?? Number.MAX_SAFE_INTEGER) -
      (orderById.get(right.characterId) ?? Number.MAX_SAFE_INTEGER),
  );
};

export const rarityOrder: Character["rarity"][] = ["UR", "SSR", "SR", "R"];

export const resourceGroupLabels: Record<ResourceGroup, string> = {
  UR: "UR",
  SSR_LIMITED: "限定 SSR",
  SSR: "普通 SSR",
  SR: "SR",
  R: "R",
};

export const resourceGroupOptions = [
  { value: "全部", label: "全部品质" },
  ...RESOURCE_GROUPS.map((value) => ({
    value,
    label: resourceGroupLabels[value],
  })),
];

export const sourceTagIds: Record<string, string[]> = {
  "source.global.attack": ["tag.attribute.attack"],
  "source.global.hp": ["tag.attribute.hp"],
  "source.global.physical-defense": ["tag.attribute.physical-defense"],
  "source.global.magic-defense": ["tag.attribute.magic-defense"],
  "source.global.all-stats": ["tag.attribute.all-stats"],
  "source.global.fire": ["tag.element.fire"],
  "source.global.wind": ["tag.element.wind"],
  "source.global.water": ["tag.element.water"],
  "source.global.earth": ["tag.element.earth"],
  "source.global.spirit": ["tag.element.spirit"],
  "source.global.all-element": ["tag.element.all"],
  "source.global.element-resilience": ["tag.attribute.element-resilience"],
  "source.global.element-resilience-break": [
    "tag.attribute.element-resilience-break",
  ],
};

export const derivedTagIds = (
  workbench: Workbench,
  wheel: Pick<Wheel, "perStarGains" | "perRankGains">,
): string[] => {
  const available = new Set(
    workbench.wheelTags.filter((tag) => tag.isEnabled).map((tag) => tag.id),
  );
  const found = new Set(
    [...wheel.perStarGains, ...wheel.perRankGains]
      .flatMap((gain) => sourceTagIds[gain.sourceAttributeId] ?? [])
      .filter((id) => available.has(id)),
  );
  return [...workbench.wheelTags]
    .sort((left, right) => left.order - right.order)
    .map((tag) => tag.id)
    .filter((id) => found.has(id));
};

export const withDerivedWheelTags = (workbench: Workbench): Workbench => ({
  ...workbench,
  fateWheels: workbench.fateWheels.map((wheel) => ({
    ...wheel,
    tagIds: derivedTagIds(workbench, wheel),
  })),
  defaults: {
    ...workbench.defaults,
    fateWheel: {
      ...workbench.defaults.fateWheel,
      tagIds: derivedTagIds(workbench, workbench.defaults.fateWheel),
    },
  },
});

export const tagNames = (workbench: Workbench, wheel: Wheel): string[] =>
  wheelTagNames(
    { ...wheel, tagIds: derivedTagIds(workbench, wheel) },
    workbench.wheelTags,
  );

export const tagOptions = (workbench: Workbench): FilterOption[] => [
  { value: "全部", label: "全部标签" },
  ...workbench.wheelTags
    .filter((tag) => tag.isEnabled)
    .sort((left, right) => left.order - right.order)
    .map((tag) => ({ value: tag.id, label: compactWheelTagName(tag.name) })),
];

export const focusableSelector =
  'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [href], [tabindex]:not([tabindex="-1"])';

export function useDialogFocus(open: boolean, onClose: () => void) {
  useBrowserDismiss(open,onClose);
  const dialogRef = useRef<HTMLElement | null>(null);
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  }, [onClose]);
  useEffect(() => {
    if (!open) return undefined;
    const previouslyFocused =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const getFocusable = (): HTMLElement[] =>
      dialogRef.current
        ? Array.from(
            dialogRef.current.querySelectorAll<HTMLElement>(focusableSelector),
          ).filter(
            (element) =>
              element.getClientRects().length > 0 &&
              element.getAttribute("aria-hidden") !== "true",
          )
        : [];
    const isTopmost = (): boolean => {
      const dialogs = Array.from(
        document.querySelectorAll<HTMLElement>('[aria-modal="true"]'),
      );
      return dialogs.at(-1) === dialogRef.current;
    };
    const focusFrame = window.requestAnimationFrame(() => {
      const dialog = dialogRef.current;
      if (!dialog || !isTopmost()) return;
      const preferred = dialog.querySelector<HTMLElement>(
        "[data-dialog-autofocus], [autofocus]",
      );
      (preferred ?? getFocusable()[0] ?? dialog).focus({ preventScroll: true });
    });
    const onKeyDown = (event: KeyboardEvent): void => {
      const dialog = dialogRef.current;
      if (!dialog || !isTopmost()) return;
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        closeRef.current();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = getFocusable();
      if (!focusable.length) {
        event.preventDefault();
        dialog.focus({ preventScroll: true });
        return;
      }
      // Own Tab before Fluent's document-level focus manager. Otherwise it can
      // move focus to the body before the boundary check sees the last button.
      event.preventDefault();
      event.stopPropagation();
      const index=focusable.indexOf(document.activeElement as HTMLElement);
      const next=index<0?(event.shiftKey?focusable.length-1:0):(index+(event.shiftKey?-1:1)+focusable.length)%focusable.length;
      focusable[next]!.focus({preventScroll:true});
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      window.removeEventListener("keydown", onKeyDown, true);
      if (previouslyFocused?.isConnected)
        previouslyFocused.focus({ preventScroll: true });
    };
  }, [open]);
  return dialogRef;
}

export interface PageProps {
  workbench: Workbench;
  userPlan: UserPlan;
  setWorkbench(next: Workbench | ((current: Workbench) => Workbench)): void;
  setUserPlan(next: UserPlan | ((current: UserPlan) => UserPlan)): void;
  solve(): Promise<void>;
  solverProgress: SolverProgressV2 | undefined;
  solverAllowed: boolean;
  notify(message: string): void;
  confirmAction(request: ConfirmRequest): void;
  navigate(page: Page): void;
  importResults(): Promise<void>;
  exportAllResults(): Promise<void>;
  requestedHistoryId?: string;
}

export function solverResultStatus(result: SolverResult | undefined): string {
  if (!result || result.status === "not_run") return "尚未计算";
  if (result.isProvenOptimal) return "已证明最优";
  if (result.terminationReason === "user_stop") return "已停止 · 未证明最优";
  if (result.terminationReason === "engine_error") return "异常结束 · 已保留可行方案";
  if (result.status === "time_limit") return "限时可行";
  if (result.status === "cancelled") return "已取消";
  if (result.status === "failed") return "失败";
  return "可行解";
}

export interface FilterOption {
  value: string;
  label: string;
}

